import React, { useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import {
  useGetDeliveryV2Query,
  useGetProfileQuery,
  DeliveryStatus,
  GetProfileQuery,
  GetDeliveryV2Query,
} from '@fdha/graphql-api-patient';
import { Loader } from '@fdha/web-ui-library';
import { useTranslation } from 'react-i18next';
import { parseISO } from 'date-fns';

import { MealDeliveryProvider } from '../../hooks/useMealDeliveryContext';

export interface MealDeliveryContextData {
  deliveryId: string;
  deliveryData: GetDeliveryV2Query;
  profileData: GetProfileQuery;
  isLoading: boolean;
  isCanceled?: boolean;
  deliveryStatus?: DeliveryStatus | null;
  isoDeliveryDate?: string;
  patientId: string;
  foodProgramUserId: string;
  deliveryMenuId: string;
  allowSelfPreparedMeals: boolean;
  headerTitle: {
    i18nKey: string;
    i18nParams: { [key: string]: string };
    fallback: string;
  };
}

const MealDeliveryLayout: React.FC = () => {
  const params = useParams();
  const deliveryId = params.id || '';
  const { t } = useTranslation();

  const { data: deliveryData, loading: deliveryLoading } =
    useGetDeliveryV2Query({
      variables: {
        deliveryId,
      },
      skip: !deliveryId,
      fetchPolicy: 'cache-and-network',
    });

  const { data: profileData, loading: profileLoading } = useGetProfileQuery({
    fetchPolicy: 'cache-and-network',
  });

  const isLoading = deliveryLoading || profileLoading;

  const isCanceled = deliveryData?.deliveryV2?.isCanceled;
  const deliveryStatus = deliveryData?.deliveryV2?.status;
  const isoDeliveryDate = deliveryData?.deliveryV2?.deliveryDate;
  const patientId = deliveryData?.deliveryV2?.foodProgramUser?.patient?.id;
  const foodProgramUserId = deliveryData?.deliveryV2?.foodProgramUser?.id;
  const deliveryMenuId = deliveryData?.deliveryV2?.dietPlanDeliveryMenu?.id;
  const allowSelfPreparedMeals =
    profileData?.me?.eligibleForHomeCooked || false;

  const headerTitle = useMemo(() => {
    const isDeliveryClosed = deliveryStatus === DeliveryStatus.Closed;
    const showDeliveryPreview = isDeliveryClosed || isCanceled;

    if (!showDeliveryPreview) {
      const translatedText = isoDeliveryDate
        ? t('meals:editDelivery.title', {
            date: parseISO(isoDeliveryDate),
          })
        : 'Delivery expected';

      return {
        i18nKey: `meals:editDelivery.title`,
        i18nParams: { date: isoDeliveryDate || '' },
        fallback: translatedText,
      };
    }

    const keySuffix = isCanceled ? 'canceledTitle' : 'title';
    const translatedText = isoDeliveryDate
      ? t(`meals:deliveryPreview.${keySuffix}`, {
          date: parseISO(isoDeliveryDate),
        })
      : `Delivery ${isCanceled ? 'canceled' : 'expected'}`;

    return {
      i18nKey: `meals:deliveryPreview.${keySuffix}`,
      i18nParams: { date: isoDeliveryDate || '' },
      fallback: translatedText,
    };
  }, [isoDeliveryDate, deliveryStatus, isCanceled, t]);

  if (isLoading) {
    return <Loader />;
  }

  // Check if we have the required data
  if (!patientId || !foodProgramUserId || !deliveryMenuId) {
    return <Loader />;
  }

  const sharedData = {
    deliveryId,
    deliveryData,
    profileData,
    isLoading,
    isCanceled,
    deliveryStatus,
    isoDeliveryDate,
    patientId,
    foodProgramUserId,
    deliveryMenuId,
    allowSelfPreparedMeals,
    headerTitle,
  };

  return (
    <MealDeliveryProvider
      patientId={patientId}
      dietPlanDeliveryMenuId={deliveryMenuId}
      foodProgramUserId={foodProgramUserId}
      deliveryId={deliveryId}
      deliveryStatus={deliveryStatus}
      isCanceled={isCanceled}
    >
      <Outlet context={sharedData} />
    </MealDeliveryProvider>
  );
};

export default MealDeliveryLayout;
